import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getRecording, mediaBlob, noteDownload, deleteRecording, keepRecording, renameSpeakers, createSummary,
  extractTodos, createTodoList, notReady, reasonOf, clockOf, spokenLength, languageOf,
  transcriptWords, transcriptText, transcriptTrouble, captureWords
} from '../../lib/recordings';
import { longDay } from '../../lib/quickAdd';
import { relative } from '../../lib/when';
import { useMe } from '../../lib/me';
import { useFeature } from '../../lib/features';
import { CONTRACTOR_ID_KEY, contractorSignedIn } from '../../lib/contractorAuth';
import { Avatar, Dialog, Faces, GrowingTextarea } from '../todos/TodoPeople';
import { Icon } from '../todos/icons';
import Markdown from './Markdown';
import { KindIcon, StateChip, ExpiryChip, NotReadyPanel, whenOf } from './RecordingsPage';
import TodosModeChoice from '../meetings/TodosMode';
import SpeakerNaming from '../meetings/RoomSpeakers';
import useRecordingReason from './useRecordingReason';
import './Recordings.css';
import '../meetings/Meetings.css';

/**
 * ONE RECORDING: the player, the transcript in step with it, and what can be
 * made from the transcript (Gordon's points 3 and 4, 22 October).
 *
 * THE PLAYER AND THE TRANSCRIPT ARE ONE THING. The segment being spoken is
 * highlighted as the recording plays and the list follows it; a timestamp
 * seeks the player; a speaker's name is a button, because the transcriber
 * only knows "Speaker 2" and the person who was there knows it was Rita -
 * one rename, and every one of her lines says so, here and on the server.
 * Space plays and pauses, the arrows skip five seconds, and 1x / 1.5x / 2x
 * is a row of buttons rather than a menu, because 1.5x is how everybody
 * listens to a recording of a meeting they were in.
 *
 * The media is fetched with the sign-in and played from an object URL, the
 * way chat's voice notes are: a <video src> carries no token.
 *
 * SUMMARY AND TO-DOS are asked of the server, with an optional instruction
 * ("focus on decisions and who does what"), and every answer is kept with
 * its instruction so the history is readable. The summary is Markdown,
 * drawn through Markdown.js and never as HTML. The to-dos come back as a
 * proposal per person - tick-boxes, all ticked, the titles editable, the
 * assignee changeable among the people who were on the call - and "Create
 * shared list" makes one to-do list shared with the call's members.
 *
 * Phones: the player on top, then one set of tabs - Transcript, Summary,
 * To-dos. On a wide screen the transcript keeps the left column and the two
 * panels share the right.
 */

const NARROW = '(max-width: 860px)';
const RATES = [1, 1.5, 2];

function useNarrow() {
  const [narrow, setNarrow] = useState(() => {
    try { return window.matchMedia(NARROW).matches; } catch { return false; }
  });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(NARROW); } catch { return undefined; }
    const on = () => setNarrow(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on));
  }, []);
  return narrow;
}

/** The person signed in, as the chat names people: 'u:<id>' or 'c:<id>'. */
const myKeys = (me) => {
  const keys = [];
  if (me?.id) keys.push(`u:${me.id}`);
  try {
    const c = localStorage.getItem(CONTRACTOR_ID_KEY);
    if (c && contractorSignedIn()) keys.push(`c:${c}`);
  } catch { /* storage refused */ }
  return keys;
};

/** The words with the search term marked, as elements - never as HTML. */
const marked = (text, q) => {
  if (!q) return text;
  const t = String(text || '');
  const lower = t.toLowerCase();
  const needle = q.toLowerCase();
  const out = [];
  let i = 0;
  let at = lower.indexOf(needle);
  while (at >= 0) {
    if (at > i) out.push(t.slice(i, at));
    out.push(<mark key={at}>{t.slice(at, at + needle.length)}</mark>);
    i = at + needle.length;
    at = lower.indexOf(needle, i);
  }
  if (i < t.length) out.push(t.slice(i));
  return out;
};

const download = (name, text) => {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
};

/** A proposal from the server, as the editable draft the panel works on. */
const draftFrom = (summary, attendees) => {
  const people = Array.isArray(summary?.json?.people) ? summary.json.people : [];
  return {
    sid: summary?.id || null,
    people: people.map((p, i) => ({
      id: `${i}`,
      name: p.name || 'Unassigned',
      key: p.key || (attendees.find((a) => a.name === p.name)?.key) || '',
      items: (Array.isArray(p.items) ? p.items : []).map((it, j) => ({
        id: `${i}-${j}`, on: true, title: it.title || '', due_hint: it.due_hint || '', notes: it.notes || '',
        // The day the server worked out from what was said and the call's own day.
        due_date: it.due_date || null, due_time: it.due_time || null
      }))
    }))
  };
};

export default function RecordingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const me = useMe();
  const narrow = useNarrow();
  const admin = me?.isAdmin === true;
  const todoPath = contractorSignedIn() && !me?.signedIn ? '/todo' : '/todos';
  // 078, Settings -> Features: with the AI off nothing new is asked of
  // Claude - the forms give way to a note, and what was made before stays
  // readable; with To do off a to-do list cannot be made from it.
  const aiOn = useFeature('ai');
  const todosOn = useFeature('todos');

  const [rec, setRec] = useState(null);
  const [error, setError] = useState(null);
  const [notThere, setNotThere] = useState(false);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [mediaTrouble, setMediaTrouble] = useState(null);
  const mediaRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  const [length, setLength] = useState(0);
  const [rate, setRate] = useState(1);
  const [q, setQ] = useState('');
  const [renaming, setRenaming] = useState(null); // { from, to }
  const [tab, setTab] = useState(narrow ? 'transcript' : 'summary');
  const [summaryPrompt, setSummaryPrompt] = useState('');
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [todoPrompt, setTodoPrompt] = useState('');
  const [todoBusy, setTodoBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [creating, setCreating] = useState(false);
  // ONE SHARED LIST OR A PRIVATE LIST EACH (Gordon, 22 October; see
  // components/meetings/TodosMode). A meeting's recording starts on what
  // its organiser chose; a call's on shared, as it always was.
  const [todosMode, setTodosMode] = useState('shared');
  // NAMING THE SPEAKERS (components/meetings/RoomSpeakers): a panel above
  // the transcript, open by itself while any voice is still "Speaker n",
  // folded away once everybody has a name - and openable again.
  const [naming, setNaming] = useState(null); // null: not yet decided
  const [toast, setToast] = useState(null);
  const [panelError, setPanelError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [keeping, setKeeping] = useState(false);
  const [keepUntil, setKeepUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);
  // Not on the call: the reason first, once a day (3 Nov). `asked` is
  // turned to open it again after a question that was cancelled.
  const [withReason, reasonDialog] = useRecordingReason();
  const [asked, setAsked] = useState(0);
  const [noReason, setNoReason] = useState(false);

  /* The toast stays long enough to be read and pressed, then goes. */
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 12000);
    return () => clearTimeout(t);
  }, [toast]);

  /* ---- the recording */
  useEffect(() => {
    let gone = false;
    setRec(null);
    setError(null);
    setNoReason(false);
    withReason(id, () => getRecording(id), 'transcript').then((r) => {
      if (gone) return;
      setRec(r);
      if (r.todos_mode === 'personal' || r.meeting?.todos_mode === 'personal') setTodosMode('personal');
      const todos = (r.summaries || []).filter((s) => s.kind === 'todos' && s.json);
      if (todos.length) setDraft(draftFrom(todos[todos.length - 1], r.attendees || []));
    }).catch((err) => {
      if (gone) return;
      if (notReady(err)) setNotThere(true);
      else if (err.response?.status === 428) setNoReason(true);
      else setError(err.response?.status === 404 ? 'That recording is not there - it may have expired or been deleted.'
        : reasonOf(err, 'Could not open that recording.'));
    });
    return () => { gone = true; };
  }, [id, asked]); // eslint-disable-line react-hooks/exhaustive-deps

  /* A recording still transcribing is looked at again every few seconds. */
  useEffect(() => {
    if (!rec || !/Transcrib|Recording/.test(transcriptWords(rec))) return undefined;
    const t = setInterval(() => getRecording(id).then((r) => setRec(r)).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [rec, id]);

  /* ---- the media, as a blob */
  useEffect(() => {
    if (!rec || rec.state === 'recording' || rec.state === 'asking') return undefined;
    let url = null;
    let gone = false;
    withReason(id, () => mediaBlob(id), 'audio').then((blob) => {
      if (gone) return;
      url = URL.createObjectURL(blob);
      setMediaUrl(url);
    }).catch((err) => {
      if (gone) return;
      setMediaTrouble(err.response?.data?.gone || err.response?.status === 404
        ? 'The recording itself has gone - deleted, or past its retention. The transcript stays.'
        : reasonOf(err, 'The recording could not be loaded.'));
    });
    return () => { gone = true; if (url) URL.revokeObjectURL(url); };
  }, [id, rec?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (mediaRef.current) mediaRef.current.playbackRate = rate; }, [rate, mediaUrl]);
  useEffect(() => { setTab((t) => (narrow ? t : (t === 'transcript' ? 'summary' : t))); }, [narrow]);

  const seek = useCallback((ms) => {
    const el = mediaRef.current;
    if (!el) return;
    el.currentTime = Math.max(0, ms / 1000);
    setAt(el.currentTime);
    el.play().catch(() => {});
  }, []);

  const togglePlay = useCallback(() => {
    const el = mediaRef.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {}); else el.pause();
  }, []);

  /* Space, and the arrows, from anywhere on the page that is not a field. */
  useEffect(() => {
    const onKey = (e) => {
      const tgt = e.target;
      const tag = tgt?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tgt?.isContentEditable) return;
      if (tgt === mediaRef.current || (tag === 'BUTTON' && e.key === ' ')) return;
      if (document.querySelector('.tds-dialog')) return;
      if (e.key === ' ' || e.key === 'k') { e.preventDefault(); togglePlay(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); seek((mediaRef.current?.currentTime || 0) * 1000 + 5000); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); seek((mediaRef.current?.currentTime || 0) * 1000 - 5000); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [togglePlay, seek]);

  /* ---- the transcript */
  const segments = useMemo(() => (rec?.segments || []).slice()
    .sort((a, b) => (a.start_ms || 0) - (b.start_ms || 0)), [rec]);
  const languages = useMemo(() => [...new Set(segments.map((s) => s.language).filter(Boolean))], [segments]);
  const current = useMemo(() => {
    const ms = at * 1000;
    let idx = -1;
    for (let i = 0; i < segments.length; i += 1) {
      if ((segments[i].start_ms || 0) <= ms) idx = i; else break;
    }
    if (idx >= 0 && segments[idx].end_ms && ms > segments[idx].end_ms + 1500) return -1;
    return idx;
  }, [segments, at]);
  const needle = q.trim();
  const shown = useMemo(() => (needle
    ? segments.filter((s) => String(s.text || '').toLowerCase().includes(needle.toLowerCase())
      || String(s.speaker || '').toLowerCase().includes(needle.toLowerCase()))
    : segments), [segments, needle]);

  const listRef = useRef(null);
  useEffect(() => {
    if (!playing || current < 0 || needle) return;
    const el = listRef.current?.querySelector(`[data-seq="${segments[current].seq}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [current, playing, needle, segments]);

  const rename = async () => {
    const from = renaming?.from;
    const to = (renaming?.to || '').trim();
    const sid = renaming?.speakerId || null;
    setRenaming(null);
    if (!from || !to || to === from) return;
    const before = rec;
    // By the transcriber's own id for the voice where the segment carries
    // one, so two people it called "Speaker 1" at different times are not
    // confused; by the label otherwise.
    const isTheirs = (s) => (sid ? s.speaker_id === sid : s.speaker === from);
    setRec((r) => ({ ...r, segments: (r.segments || []).map((s) => (isTheirs(s) ? { ...s, speaker: to } : s)) }));
    try {
      await renameSpeakers(id, { [sid || from]: to });
    } catch (err) {
      setRec(before);
      setPanelError(reasonOf(err, 'Could not rename them.'));
    }
  };

  const text = () => transcriptText(rec, segments);
  const copy = async (what, value) => {
    setCopied((await copyText(value)) ? what : null);
    setTimeout(() => setCopied(null), 1800);
  };

  /* ---- summary */
  const summaries = useMemo(() => (rec?.summaries || []).filter((s) => !s.kind || s.kind === 'summary')
    .slice().sort((a, b) => (Date.parse(b.created_at || 0) || 0) - (Date.parse(a.created_at || 0) || 0)), [rec]);

  const summarise = async () => {
    setSummaryBusy(true);
    setPanelError(null);
    try {
      const s = await withReason(id, () => createSummary(id, summaryPrompt.trim()), 'summary');
      setRec((r) => ({ ...r, summaries: [...(r.summaries || []), { kind: 'summary', ...s }], has_summary: true }));
      setSummaryPrompt('');
    } catch (err) {
      setPanelError(reasonOf(err, 'Could not write a summary.'));
    } finally {
      setSummaryBusy(false);
    }
  };

  /* ---- to-dos */
  const attendees = rec?.attendees || [];
  const extract = async () => {
    setTodoBusy(true);
    setPanelError(null);
    try {
      const s = await withReason(id, () => extractTodos(id, todoPrompt.trim()), 'todos');
      setDraft(draftFrom(s, attendees));
      setRec((r) => ({ ...r, summaries: [...(r.summaries || []), { kind: 'todos', ...s }], has_todos: true }));
    } catch (err) {
      setPanelError(reasonOf(err, 'Could not pick the to-dos out.'));
    } finally {
      setTodoBusy(false);
    }
  };

  const patchItem = (pid, iid, patch) => setDraft((d) => ({ ...d, people: d.people.map((p) => (p.id !== pid ? p
    : { ...p, items: p.items.map((it) => (it.id !== iid ? it : { ...it, ...patch })) })) }));
  const assign = (pid, key) => setDraft((d) => ({ ...d, people: d.people.map((p) => (p.id !== pid ? p
    : { ...p, key, name: key ? (attendees.find((a) => a.key === key)?.name || p.name) : 'Unassigned' })) }));

  const selection = useMemo(() => (draft?.people || []).flatMap((p) => p.items.filter((it) => it.on && it.title.trim())
    .map((it) => ({ key: p.key || null, name: p.key ? p.name : null, title: it.title.trim(),
      due_hint: it.due_hint || null, notes: it.notes || null, due_date: it.due_date || null, due_time: it.due_time || null }))), [draft]);

  const createList = async () => {
    if (!draft?.sid || !selection.length) return;
    setCreating(true);
    setPanelError(null);
    try {
      const r = await withReason(id, () => createTodoList(id, draft.sid, selection, todosMode), 'todos');
      // Somebody the list could not be shared with (a contractor the firm's
      // chat rules keep apart, say): their items were made unassigned, and
      // the toast says so by name rather than letting them look lost.
      const skipped = (Array.isArray(r?.skipped) ? r.skipped : []).map((x) => x.name).filter(Boolean);
      setToast({ list: r?.list_id, n: r?.created ?? selection.length, name: r?.name || null, skipped,
        mode: r?.mode || todosMode, perPerson: r?.per_person || null });
    } catch (err) {
      setPanelError(reasonOf(err, 'Could not create the list.'));
    } finally {
      setCreating(false);
    }
  };

  /* ---- delete, keep */
  const keys = myKeys(me);
  const canDelete = admin || rec?.started_by_me === true || (rec?.started_by_key && keys.includes(rec.started_by_key))
    || rec?.can_delete === true;

  const remove = async () => {
    setBusy(true);
    try {
      await deleteRecording(id);
      navigate('/recordings', { replace: true });
    } catch (err) {
      setConfirming(false);
      setPanelError(reasonOf(err, 'Could not delete it.'));
    } finally {
      setBusy(false);
    }
  };

  const keep = async (until) => {
    setBusy(true);
    try {
      const r = await keepRecording(id, until);
      setRec((cur) => ({ ...cur, keep_until: r?.keep_until !== undefined ? r.keep_until : until,
        expires_at: r?.expires_at !== undefined ? r.expires_at : cur.expires_at }));
      setKeeping(false);
    } catch (err) {
      setPanelError(reasonOf(err, 'Could not change how long it is kept.'));
    } finally {
      setBusy(false);
    }
  };

  /* ---------------------------------------------------------------- render */

  if (notThere) return <div className="rc-page"><NotReadyPanel /></div>;
  if (error) {
    return (
      <div className="rc-page">
        <p className="rc-error" role="alert">{error}</p>
        <Link className="rc-back" to="/recordings">Back to recordings</Link>
      </div>
    );
  }
  if (noReason) {
    // The question was cancelled: nothing was opened, and it can be asked again.
    return (
      <div className="rc-page" data-testid="recording-no-reason">
        <p className="rc-quiet" role="status">You were not on this call, so Jarvis asks why before it opens. Nothing was opened.</p>
        <p>
          <button type="button" className="btn btn-primary tds-btn" onClick={() => setAsked((n) => n + 1)}>Say why and open it</button>
          {' '}<Link className="rc-back" to="/recordings">Back to recordings</Link>
        </p>
      </div>
    );
  }
  if (!rec) return <div className="rc-page"><p className="rc-quiet">Loading…</p>{reasonDialog}</div>;

  const about = rec.about || {};
  const ready = /Ready/.test(transcriptWords(rec)) || segments.length > 0;
  const tabs = narrow ? [['transcript', 'Transcript'], ['summary', 'Summary'], ['todos', 'To-dos']]
    : [['summary', 'Summary'], ['todos', 'To-dos']];

  const unnamed = segments.some((s) => /^Speaker \d+$/i.test(String(s.speaker || '')) || !s.speaker);
  const namingOpen = naming === null ? unnamed : naming;
  const speakerPanel = segments.length > 0 && (
    <div className="rc-naming" data-open={namingOpen || undefined}>
      <button type="button" className="tds-link rc-naming-toggle" aria-expanded={namingOpen} onClick={() => setNaming(!namingOpen)}>
        {namingOpen ? 'Hide' : 'Name the speakers'}{!namingOpen && unnamed ? ' (some are still numbered)' : ''}
      </button>
      {namingOpen && (
        <SpeakerNaming recording={rec} mediaUrl={mediaUrl}
          onRenamed={(map) => setRec((r) => ({ ...r, segments: (r.segments || []).map((s) => {
            const to = map[s.speaker_id] || map[s.speaker];
            return to ? { ...s, speaker: to } : s;
          }) }))} />
      )}
    </div>
  );

  const transcript = (
    <section className="rc-transcript" aria-label="Transcript">
      {speakerPanel}
      <div className="rc-tx-head">
        <h2 className="rc-h2">Transcript</h2>
        {languages.length > 0 && (
          <span className="rc-langs">
            {languages.map((l) => <span key={l} className="rc-chip is-lang" data-lang={l}>{languageOf(l)}</span>)}
          </span>
        )}
        <span className="rc-tx-tools">
          <button type="button" className="tds-link rc-tool" disabled={!segments.length} onClick={() => copy('transcript', text())}>
            {copied === 'transcript' ? 'Copied' : 'Copy'}
          </button>
          <button type="button" className="tds-link rc-tool" disabled={!segments.length}
            onClick={() => { download(`${(about.label || 'call').replace(/[^\w.-]+/g, '_')}-transcript.txt`, text()); withReason(id, () => noteDownload(id), 'download').catch(() => null); }}>
            Download
          </button>
        </span>
      </div>
      {segments.length > 0 && (
        <label className="rc-search rc-tx-search">
          <Icon name="search" size={15} />
          <input type="search" value={q} placeholder="Search the transcript" aria-label="Search the transcript"
            onChange={(e) => setQ(e.target.value)} />
          {needle && <span className="rc-tx-count">{shown.length} of {segments.length}</span>}
        </label>
      )}
      {segments.length === 0 ? (
        <p className="rc-quiet rc-tx-empty" data-trouble={rec.transcript_error ? 'yes' : undefined}>
          {transcriptTrouble(rec)
            || (/Transcrib|Recording/.test(transcriptWords(rec)) ? 'The transcript is being written. It appears here when it is ready.'
              : /Failed/.test(transcriptWords(rec)) ? 'The transcript could not be written for this recording.'
                : 'There is no transcript for this recording.')}
        </p>
      ) : shown.length === 0 ? (
        <p className="rc-quiet rc-tx-empty">Nothing in the transcript says that.</p>
      ) : (
        <ol className="rc-tx" ref={listRef}>
          {shown.map((s, i) => {
            const prev = shown[i - 1];
            const sameSpeaker = prev && prev.speaker === s.speaker && !needle;
            const isCurrent = segments[current] === s;
            return (
              <li key={s.seq} className={`rc-seg${isCurrent ? ' is-current' : ''}${sameSpeaker ? ' is-cont' : ''}`}
                data-seq={s.seq} aria-current={isCurrent || undefined}>
                <button type="button" className="rc-seg-time" onClick={() => seek(s.start_ms)}
                  title="Play from here">{clockOf(s.start_ms)}</button>
                <span className="rc-seg-body">
                  {renaming && renaming.from === s.speaker && renaming.seq === s.seq ? (
                    <form className="rc-rename" onSubmit={(e) => { e.preventDefault(); rename(); }}>
                      <input autoFocus value={renaming.to} aria-label={`Rename ${s.speaker}`}
                        onChange={(e) => setRenaming({ ...renaming, to: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(null); }} />
                      <button type="submit" className="tds-link">Save</button>
                      <button type="button" className="tds-link" onClick={() => setRenaming(null)}>Cancel</button>
                    </form>
                  ) : (
                    <button type="button" className="rc-seg-speaker" data-speaker={s.speaker || ''}
                      title="Rename this speaker everywhere"
                      onClick={() => setRenaming({ from: s.speaker, to: s.speaker || '', seq: s.seq, speakerId: s.speaker_id || null })}>
                      {s.speaker || 'Unknown'}
                    </button>
                  )}
                  <span className="rc-seg-text">{marked(s.text, needle)}</span>
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );

  const summaryPanel = (
    <section className="rc-panel" aria-label="Summary" data-panel="summary">
      {!aiOn ? (
        <p className="rc-ai-off" role="note">
          AI summaries are switched off by your administrator. The transcript is still here
          {summaries.length ? ', and so are the summaries made before' : ''}.
        </p>
      ) : (
      <form className="rc-ask" onSubmit={(e) => { e.preventDefault(); summarise(); }}>
        <label className="tds-field">
          <span>Instruction (optional)</span>
          <GrowingTextarea rows={2} maxLength={1000} value={summaryPrompt} aria-label="Instruction for the summary"
            placeholder="e.g. focus on decisions and who does what"
            onChange={(e) => setSummaryPrompt(e.target.value)} disabled={!ready || summaryBusy} />
        </label>
        <button type="submit" className="btn btn-primary tds-btn rc-make" disabled={!ready || summaryBusy}>
          {summaryBusy ? 'Writing…' : summaries.length ? 'Create another summary' : 'Create a summary'}
        </button>
        {!ready && <p className="rc-quiet">A summary needs the transcript, which is not ready yet.</p>}
      </form>
      )}
      {aiOn && summaries.length === 0 && ready && (
        <p className="rc-quiet">No summary yet. Say what to focus on, or just press the button.</p>
      )}
      {summaries.map((s, i) => (
        <article key={s.id || i} className="rc-summary" data-summary={s.id}>
          <header className="rc-summary-head">
            <span className="rc-summary-when">
              {s.created_at ? relative(s.created_at) : ''}{s.created_by_name ? ` · ${s.created_by_name}` : ''}
              {s.model ? ` · ${s.model}` : ''}
            </span>
            <button type="button" className="tds-link rc-tool" onClick={() => copy(`s-${s.id}`, s.body || '')}>
              {copied === `s-${s.id}` ? 'Copied' : 'Copy'}
            </button>
          </header>
          {s.prompt && <p className="rc-summary-prompt">“{s.prompt}”</p>}
          <Markdown text={s.body} />
        </article>
      ))}
    </section>
  );

  const todosPanel = (
    <section className="rc-panel" aria-label="To-dos" data-panel="todos">
      {!aiOn ? (
        <p className="rc-ai-off" role="note">
          AI summaries are switched off by your administrator, so to-dos are not picked out of the
          transcript. The transcript is still here.
        </p>
      ) : (
      <form className="rc-ask" onSubmit={(e) => { e.preventDefault(); extract(); }}>
        <label className="tds-field">
          <span>Instruction (optional)</span>
          <GrowingTextarea rows={2} maxLength={1000} value={todoPrompt} aria-label="Instruction for the to-dos"
            placeholder="e.g. only what was agreed for this week"
            onChange={(e) => setTodoPrompt(e.target.value)} disabled={!ready || todoBusy} />
        </label>
        <button type="submit" className="btn btn-primary tds-btn rc-make" disabled={!ready || todoBusy}>
          {todoBusy ? 'Reading…' : draft ? 'Extract again' : 'Extract to-dos'}
        </button>
        {!ready && <p className="rc-quiet">To-dos come from the transcript, which is not ready yet.</p>}
      </form>
      )}

      {draft && !todosOn && (
        <p className="rc-ai-off" role="note">To do is switched off by your administrator, so these cannot be made into to-dos.</p>
      )}
      {draft && todosOn && (
        <div className="rc-draft">
          <p className="rc-quiet">
            What the call asked of each person. Untick what is wrong, edit the words, move an item to
            somebody else, then choose how the lists are made.
          </p>
          {draft.people.length === 0 && <p className="rc-quiet">Nothing in the transcript reads as a to-do.</p>}
          {draft.people.map((p) => (
            <div key={p.id} className="rc-person" data-person={p.key || 'unassigned'}>
              <div className="rc-person-head">
                <Avatar name={p.key ? p.name : '?'} who={p.key || 'unassigned'} />
                <select className="rc-assign" value={p.key || ''} aria-label={`Who these are for`}
                  onChange={(e) => assign(p.id, e.target.value)}>
                  <option value="">Unassigned</option>
                  {attendees.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
                  {p.key && !attendees.some((a) => a.key === p.key) && <option value={p.key}>{p.name}</option>}
                </select>
                <span className="rc-person-n">{p.items.filter((it) => it.on).length} of {p.items.length}</span>
              </div>
              <ul className="rc-items">
                {p.items.map((it) => (
                  <li key={it.id} className={`rc-item${it.on ? '' : ' is-off'}`}>
                    <input type="checkbox" className="rc-item-check" checked={it.on} aria-label={`Include "${it.title}"`}
                      onChange={(e) => patchItem(p.id, it.id, { on: e.target.checked })} />
                    <span className="rc-item-main">
                      <input className="rc-item-title" value={it.title} aria-label="To-do"
                        onChange={(e) => patchItem(p.id, it.id, { title: e.target.value })} />
                      {(it.due_hint || it.notes) && (
                        <span className="rc-item-meta">
                          {it.due_hint && <span className="rc-chip is-quiet" data-due={it.due_date || ''}><Icon name="calendar" size={12} /> {it.due_hint}
                            {it.due_date ? ` · ${longDay(it.due_date)}${it.due_time ? ` ${it.due_time}` : ''}` : ''}</span>}
                          {it.notes && <span className="rc-item-notes">{it.notes}</span>}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {draft.people.length > 0 && (
            <div className="mt-todos-foot">
              <TodosModeChoice value={todosMode} onChange={setTodosMode} name="rc-todos-mode" />
              <div className="rc-draft-foot">
                <span className="rc-quiet">{selection.length} to-do{selection.length === 1 ? '' : 's'} ticked</span>
                <button type="button" className="btn btn-primary tds-btn rc-create" disabled={creating || !selection.length}
                  onClick={createList}>
                  {creating ? 'Creating…' : todosMode === 'personal' ? 'Create a list each' : 'Create shared list'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );

  return (
    <div className="rc-page rc-detail" data-kind={rec.kind || 'audio'}>
      {reasonDialog}
      <nav className="rc-crumbs" aria-label="Where you are">
        <Link to="/recordings">Recordings</Link>
        <span className="rc-sep" aria-hidden="true">/</span>
        <span>{about.label || 'A call'}</span>
      </nav>

      <header className="rc-detail-head">
        <div className="rc-head-words">
          <h1 className="rc-title">
            <span className={`rc-row-kind is-${rec.kind === 'video' ? 'video' : 'audio'}`}><KindIcon kind={rec.kind} size={18} /></span>
            {about.href ? <Link to={about.href} className="rc-about-link">{about.label || 'A call'}</Link> : (about.label || 'A call')}
          </h1>
          {/* A meeting's recording: its minutes live on the meeting's page. */}
          {about.kind === 'meeting' && about.href && (
            <Link to={`${about.href}?tab=minutes`} className="rc-minutes-link tds-link">Minutes of this meeting</Link>
          )}
          <p className="rc-sub">
            <time dateTime={rec.started_at || undefined}>{whenOf(rec.started_at)}</time>
            {rec.duration_ms > 0 && <><span className="rc-sep">·</span>{spokenLength(rec.duration_ms)}</>}
            {rec.started_by_name && <><span className="rc-sep">·</span>recorded by {rec.started_by_name}</>}
          </p>
          <div className="rc-chips">
            <StateChip rec={rec} />
            <ExpiryChip rec={rec} />
            {/* The language the recorder named for the transcriber, when one was. */}
            {rec.language_hint && (
              <span className="rc-chip is-lang" title="The language chosen when recording">{languageOf(rec.language_hint)}</span>
            )}
            {/* A call recorded from another app (090): what the browser mixed.
                Microphone alone is said loudly - a transcript of half a call. */}
            {rec.capture && (
              <span className={`rc-chip ${rec.capture === 'mic' ? 'is-warn' : 'is-quiet'} rc-capture-chip`} data-capture={rec.capture}
                title={rec.consent_confirmed_at ? `The other side was told, confirmed ${whenOf(rec.consent_confirmed_at)}` : undefined}>
                {captureWords(rec.capture)}
              </span>
            )}
            <Faces members={attendees} max={5} />
          </div>
        </div>
        <div className="rc-actions">
          {admin && (
            <button type="button" className="se-flat rc-action" onClick={() => {
              const d = new Date(); d.setFullYear(d.getFullYear() + 1);
              setKeepUntil(rec.keep_until ? String(rec.keep_until).slice(0, 10) : d.toISOString().slice(0, 10));
              setKeeping(true);
            }}>
              {rec.keep_until ? 'Kept' : 'Keep'}
            </button>
          )}
          {canDelete && (
            <button type="button" className="se-flat rc-action is-danger" onClick={() => setConfirming(true)}>Delete</button>
          )}
        </div>
      </header>

      {panelError && (
        <p className="rc-error" role="alert">
          {panelError} <button type="button" className="tds-link" onClick={() => setPanelError(null)}>Dismiss</button>
        </p>
      )}

      <div className="rc-detail-body">
        <div className="rc-main">
          <div className="rc-player" data-kind={rec.kind || 'audio'}>
            {mediaTrouble ? (
              <p className="rc-quiet rc-player-note">{mediaTrouble}</p>
            ) : !mediaUrl ? (
              <p className="rc-quiet rc-player-note">
                {rec.state === 'recording' || rec.state === 'asking' ? 'Still recording…' : 'Loading the recording…'}
              </p>
            ) : rec.kind === 'video' ? (
              <video ref={mediaRef} className="rc-media" src={mediaUrl} controls playsInline preload="metadata"
                onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
                onTimeUpdate={(e) => setAt(e.currentTarget.currentTime)}
                onLoadedMetadata={(e) => { if (Number.isFinite(e.currentTarget.duration)) setLength(e.currentTarget.duration); }} />
            ) : (
              <div className="rc-audio">
                <span className="rc-audio-ic" aria-hidden="true"><KindIcon kind="audio" size={26} /></span>
                <span className="rc-audio-words">
                  <span className="rc-audio-title">{about.label || 'Recording'}</span>
                  <span className="rc-audio-time">
                    {clockOf(at * 1000)}{length ? ` / ${clockOf(length * 1000)}` : rec.duration_ms ? ` / ${clockOf(rec.duration_ms)}` : ''}
                  </span>
                </span>
                <audio ref={mediaRef} className="rc-media" src={mediaUrl} controls preload="metadata"
                  onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
                  onTimeUpdate={(e) => setAt(e.currentTarget.currentTime)}
                  onLoadedMetadata={(e) => { if (Number.isFinite(e.currentTarget.duration)) setLength(e.currentTarget.duration); }} />
              </div>
            )}
            <div className="rc-player-bar">
              <span className="rc-rates" role="group" aria-label="Speed">
                {RATES.map((r) => (
                  <button key={r} type="button" className="rc-rate" aria-pressed={rate === r} onClick={() => setRate(r)}>
                    {r}×
                  </button>
                ))}
              </span>
              <span className="rc-keys" aria-hidden="true">Space plays · ← → skip 5 s</span>
            </div>
          </div>
          {!narrow && transcript}
        </div>

        <aside className="rc-side">
          <div className="tds-tabs rc-tabs" role="tablist" aria-label="More about this recording">
            {tabs.map(([key, label]) => (
              <button key={key} type="button" role="tab" className="tds-tab rc-tab" aria-selected={tab === key}
                data-tab={key} onClick={() => setTab(key)}>
                {label}
                {key === 'summary' && summaries.length > 0 && <span className="tds-tab-n">{summaries.length}</span>}
                {key === 'todos' && draft && <span className="tds-tab-n">{selection.length}</span>}
              </button>
            ))}
          </div>
          {tab === 'transcript' && narrow && transcript}
          {tab === 'summary' && summaryPanel}
          {tab === 'todos' && todosPanel}
        </aside>
      </div>

      {confirming && (
        <Dialog label="Delete this recording?" onClose={() => setConfirming(false)}>
          <p className="tds-quiet">
            The recording, its transcript and everything made from it go for good. Nobody on the call
            will be able to open it afterwards.
          </p>
          <div className="tds-dialog-foot">
            <button type="button" className="btn btn-outline tds-btn" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
            <button type="button" className="btn btn-primary tds-btn rc-confirm-delete" disabled={busy} onClick={remove}>Delete</button>
          </div>
        </Dialog>
      )}

      {keeping && (
        <Dialog label="Keep this recording" onClose={() => setKeeping(false)} className="rc-keep">
          <p className="tds-quiet">
            Recordings are kept for the firm&rsquo;s retention period and then deleted; transcripts stay.
            Keeping one holds it past that, until the day you choose.
          </p>
          <label className="tds-field">
            <span>Keep until</span>
            <input type="date" value={keepUntil} min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setKeepUntil(e.target.value)} />
          </label>
          <div className="tds-dialog-foot">
            {rec.keep_until && (
              <button type="button" className="tds-link is-danger" disabled={busy} onClick={() => keep(null)}>Stop keeping it</button>
            )}
            <button type="button" className="btn btn-outline tds-btn" disabled={busy} onClick={() => setKeeping(false)}>Cancel</button>
            <button type="button" className="btn btn-primary tds-btn rc-keep-save" disabled={busy || !keepUntil} onClick={() => keep(keepUntil)}>Keep</button>
          </div>
        </Dialog>
      )}

      {toast && (
        <div className="tds-toast rc-toast" role="status">
          <span>
            {toast.mode === 'personal'
              ? 'Created in each person\'s own Inbox'
              : `Shared list${toast.name ? ` “${toast.name}”` : ''} created`} · {toast.n} to-do{toast.n === 1 ? '' : 's'}
            {toast.mode === 'personal' && Array.isArray(toast.perPerson) && toast.perPerson.length > 0 && (
              <span className="rc-toast-skipped">{toast.perPerson.map((p) => `${p.name || p.key}: ${p.created}`).join(' · ')}</span>
            )}
            {toast.skipped?.length > 0 && (
              <span className="rc-toast-skipped">
                {toast.skipped.join(', ')} could not be added to the list; their items were created unassigned.
              </span>
            )}
          </span>
          {toast.list && (
            <Link className="tds-link" to={`${todoPath}?list=${encodeURIComponent(toast.list)}`}
              onClick={() => setToast(null)}>Open the list</Link>
          )}
          <button type="button" className="tds-x" aria-label="Dismiss" onClick={() => setToast(null)}>
            <Icon name="close" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
